import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { resolveRouteDecision, type WorkspaceKind } from '@/lib/routeGuard';
import { AppRole } from '@/lib/supabase-types';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: AppRole;
  requireClubAccess?: boolean;
  /** The workspace kind this route belongs to (venue / kiosk routes). Tenant routes keep requireClubAccess. */
  workspace?: WorkspaceKind;
  /** 'admin': owners and admins of the workspace only — kiosk staff go back to the orders. */
  workspaceRole?: 'admin';
}

// The branching lives in resolveRouteDecision (src/lib/routeGuard.ts), where
// it is unit-tested — the ordering of those checks is exactly what caused the
// Sept 2026 "perceived logout" bug (slow roles read as Access Denied).
export function ProtectedRoute({
  children,
  requiredRole,
  requireClubAccess = false,
  workspace,
  workspaceRole,
}: ProtectedRouteProps) {
  const {
    user, loading, authReady, profileSettled, profileError,
    userRoles, isIdseatAdmin, clubId, signOut, workspaces, activeWorkspace, switchWorkspace,
  } = useAuth();
  const location = useLocation();
  const neededKind: WorkspaceKind | undefined = workspace ?? (requireClubAccess ? 'tenant' : undefined);

  const decision = resolveRouteDecision({
    loading,
    authReady,
    hasUser: !!user,
    profileSettled,
    profileError,
    isIdseatAdmin,
    userRoles,
    requiredRole,
    requireClubAccess,
    clubId,
    requiredWorkspace: workspace,
    activeWorkspaceKind: activeWorkspace?.kind ?? null,
    workspaceCount: workspaces.length,
    availableWorkspaceKinds: workspaces.map((w) => w.kind),
    activeWorkspaceRole: activeWorkspace?.role ?? null,
    requiredWorkspaceRole: workspaceRole,
  });

  // 'switch': the person works in a workspace of the kind this link needs,
  // just not the active one — open it (the first of that kind) instead of
  // answering "Access Denied".
  useEffect(() => {
    if (decision !== 'switch' || !neededKind) return;
    const target = workspaces.find((w) => w.kind === neededKind);
    if (target) switchWorkspace(target.kind, target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision, neededKind]);

  // 'relogin': the session exists but the profile could not be loaded even
  // after retries. Sign this device out (a fresh login repairs it) — the
  // SIGNED_OUT event then flips the decision to 'login'.
  useEffect(() => {
    if (decision === 'relogin') {
      toast.error("Couldn't load your account — please sign in again.");
      signOut();
    }
    // signOut is stable enough for this purpose; decision is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision]);

  if (decision === 'home') {
    return <Navigate to="/kiosk" replace />;
  }

  if (decision === 'spinner' || decision === 'relogin' || decision === 'switch') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (decision === 'login') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (decision === 'unauthorized') {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
